import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { useAuthStore } from "../store/authStore";

// API 기본 설정
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 150000,
  headers: {
    "Content-Type": "application/json",
  },
});

const getAccessToken = (): {
  accessToken: string | null;
  refreshToken: string | null;
} => {
  try {
    const authState = useAuthStore.getState();
    return {
      accessToken: authState.accessToken,
      refreshToken: authState.refreshToken,
    };
  } catch (error) {
    console.error("Token retrieval error:", error);
    return { accessToken: null, refreshToken: null };
  }
};

const updateAccessToken = (newAccessToken: string) => {
  try {
    if (!newAccessToken) {
      return;
    }
    useAuthStore.getState().updateTokens({ accessToken: newAccessToken });
  } catch (error) {
    console.error("Token update error:", error);
  }
};

// 로그아웃 처리 함수
const handleLogout = () => {
  useAuthStore.getState().signOut();
  window.location.href = "/auth/signin";
};

const refreshAccessToken = async (
  retryCount: number = 0
): Promise<string | null> => {
  const { refreshToken } = getAccessToken();

  if (!refreshToken) {
    console.error("Refresh token이 없습니다. 로그아웃 처리합니다.");
    handleLogout();
    return null;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/auth/issued/token`,
      {
        headers: {
          Authorization: refreshToken,
        },
        timeout: 10000,
      }
    );

    const responseData = response.data;

    if (responseData.status && responseData.access_token) {
      updateAccessToken(responseData.access_token);
      return responseData.access_token;
    } else {
      console.error("토큰 재발급 응답이 유효하지 않습니다. 로그아웃 처리합니다.");
      handleLogout();
      return null;
    }
  } catch (error) {
    console.error("토큰 재발급 실패:", error);

    if (retryCount < 2) {
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (retryCount + 1))
      );
      return refreshAccessToken(retryCount + 1);
    }

    console.error("토큰 재발급 3회 시도 후 실패. 로그아웃 처리합니다.");
    handleLogout();
    return null;
  }
};

const isTokenExpired = (token: string, bufferMinutes: number = 5): boolean => {
  try {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split(".")[1]));
    const bufferTime = bufferMinutes * 60 * 1000;
    return payload.exp * 1000 - bufferTime < Date.now();
  } catch {
    return true;
  }
};

apiClient.interceptors.request.use(
  async (config) => {
    if (!config || !config.headers) return config;

    const { accessToken } = getAccessToken();
    if (accessToken) {
      if (isTokenExpired(accessToken, 5)) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          config.headers.Authorization = newAccessToken;
        }
      } else {
        config.headers.Authorization = accessToken as string;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response?.data?.expired === true) {
      setTimeout(async () => {
        const newAccessToken = await refreshAccessToken();
        if (!newAccessToken) {
          handleLogout();
        }
      }, 100);
    }
    return response;
  },
  async (error: AxiosError) => {
    if (!error?.config) return Promise.reject(error);

    const originalRequest = error.config as any;
    const isUnauthorized = error.response?.status === 401;
    const isExpiredResponse = error.response?.data && (error.response.data as any)?.expired === true;

    if ((isUnauthorized || isExpiredResponse) && !originalRequest._retry) {
      originalRequest._retry = true;
      const newAccessToken = await refreshAccessToken();

      if (newAccessToken) {
        if (!originalRequest.headers) {
          originalRequest.headers = {};
        }
        originalRequest.headers.Authorization = newAccessToken;
        return apiClient(originalRequest);
      } else {
        handleLogout();
      }
    }

    return Promise.reject(error);
  }
);
